"use client";

import React from "react";
import { useQuery } from "@tanstack/react-query";
import { eventsApi } from "@/lib/api/services/events.api";
import { Card, CardContent } from "@/components/ui/card";
import { Calendar, MapPin, Clock } from "lucide-react";
import { EmptyState } from "@/components/shared/empty-state";
import Link from "next/link";

interface Event {
  id: string;
  title: string;
  description: string;
  startDate: string;
  endDate: string;
  location: string;
  status: string;
  category: string;
}

export default function MyEventsPage() {
  const { data: events, isLoading } = useQuery({
    queryKey: ["my-events"],
    queryFn: async () => {
      const res = await eventsApi.list({ type: "event" });
      return ((res as unknown as { data?: Event[] }).data ?? []) as Event[];
    },
  });

  return (
    <div className="mx-auto max-w-3xl px-4 py-8">
      <div className="space-y-1 mb-6">
        <h1 className="font-heading text-xl font-semibold tracking-tight text-foreground">My Events</h1>
        <p className="text-sm text-muted-foreground leading-relaxed">Events you have registered for.</p>
      </div>

      {isLoading ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Card key={i}>
              <CardContent className="p-4">
                <div className="h-4 w-2/3 bg-muted/10 rounded animate-pulse" />
                <div className="mt-2 h-3 w-1/2 bg-muted/10 rounded animate-pulse" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : !events || events.length === 0 ? (
        <EmptyState
          title="No events found"
          description="Explore upcoming events to join."
          icon={Calendar}
          variant="guide"
        />
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {events.map((event) => (
            <Link key={event.id} href={`/events/${event.id}`}>
              <Card className="transition-colors hover:border-border/80">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-muted/10 text-muted-foreground">
                      <Calendar className="h-4 w-4" />
                    </div>
                    <div className="min-w-0">
                      <h3 className="text-sm font-medium text-foreground truncate">{event.title}</h3>
                      <p className="text-xs text-muted-foreground truncate">{event.category}</p>
                    </div>
                  </div>
                  <p className="mt-2 text-xs text-muted-foreground leading-relaxed line-clamp-2">{event.description}</p>
                  <div className="mt-3 flex items-center gap-3 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1"><Clock className="h-3 w-3 shrink-0" /> {event.startDate}</span>
                    <span className="flex items-center gap-1"><MapPin className="h-3 w-3 shrink-0" /> {event.location}</span>
                  </div>
                  <span className={`mt-3 inline-flex h-5 px-2 text-[0.6875rem] font-medium rounded-full border ${event.status === "upcoming" ? "bg-primary/10 text-primary border-primary/20" : "bg-muted/10 text-muted-foreground border-border"}`}>{event.status}</span>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
